// Recuperación automática ante chunks obsoletos tras un deploy.
//
// El problema: las páginas lazy (`/estadisticas`, `/estadisticas-admin`) se
// cargan con `React.lazy()` → viven en chunks aparte (`static/js/NNN.<hash>.js`
// y su CSS). Cuando sale un deploy nuevo, el hash de esos chunks cambia. Un
// navegador con el `index.html`/`main.js` viejo en caché pide el hash viejo,
// que ya no existe en el server → `serve -s` responde el index.html de
// fallback (MIME text/html) → el runtime de Webpack lanza `ChunkLoadError` y la
// sección no monta. No es un bug de datos: es caché vieja apuntando a un build
// que ya rotó.
//
// La cura estándar es recargar UNA vez: al refrescar baja el index.html nuevo,
// el main.js nuevo y los hashes correctos. El guard en sessionStorage evita el
// bucle infinito si tras recargar sigue fallando (ese caso ya no es caché vieja
// sino un build realmente roto, y conviene verlo en pantalla, no recargar en
// loop).

// Timestamp de la última recarga automática (ms epoch) en sessionStorage.
const RELOAD_FLAG = "afChunkReloadAt";
// Si recargamos hace menos de esto y volvió a fallar, no insistimos.
const RELOAD_WINDOW_MS = 10_000;

/**
 * Heurística para reconocer un error de carga de chunk (JS o CSS) o de import
 * dinámico, mirando el `name` y el `message` del error. Cubre los mensajes que
 * emiten Webpack (CRA), mini-css-extract-plugin y los bundlers tipo Vite/esm.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const name = (error as { name?: string }).name ?? "";
  const message = (error as { message?: string }).message ?? String(error);
  return (
    name === "ChunkLoadError" ||
    /Loading (CSS )?chunk [\w-]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message)
  );
}

/**
 * Recarga la página UNA sola vez ante un chunk obsoleto. Devuelve `true` si
 * disparó la recarga, `false` si el guard la frenó (ya recargamos hace muy poco
 * → probablemente build roto, no caché vieja).
 */
export function reloadOnceForChunkError(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_FLAG) ?? 0);
    const now = Date.now();
    if (last && now - last < RELOAD_WINDOW_MS) {
      // Ya recargamos hace muy poco y volvió a fallar → no insistir.
      return false;
    }
    window.sessionStorage.setItem(RELOAD_FLAG, String(now));
  } catch {
    // sessionStorage no disponible (modo privado / storage bloqueado):
    // recargamos igual, asumiendo que es la primera vez.
  }
  // reload(true) está deprecado; un reload normal revalida el index.html y baja
  // el bundle nuevo con los hashes correctos.
  window.location.reload();
  return true;
}

/**
 * Listener global de promesas rechazadas. Las fallas de chunk CSS
 * (mini-css-extract) y algunas de JS llegan como `unhandledrejection` fuera del
 * árbol de React, así que el ErrorBoundary no las ve — acá las cazamos también.
 * Llamar una vez al arranque (ver index.tsx).
 */
export function installChunkErrorAutoReload(): void {
  window.addEventListener("unhandledrejection", (event) => {
    if (isChunkLoadError(event.reason)) {
      // Evita el ruido en consola/Sentry: es un error esperado y recuperable.
      event.preventDefault();
      reloadOnceForChunkError();
    }
  });
}
